// PHA-1168 Reports: run one saved report. The server compiles the report to a
// fixed query (never the AI); this page shows its summary table, the detail
// rows, and exports the server's CSV.
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Box, Button, Callout, Card, Flex, Heading, Text } from '@radix-ui/themes';
import { DownloadIcon, ExclamationTriangleIcon, InfoCircledIcon } from '@radix-ui/react-icons';
import { format } from 'date-fns';
import ReportsLayout from './ReportsLayout';
import HandleChip from './HandleChip';
import { askAssistant } from '../chat';
import { useOrganization } from '../../contexts/OrganizationContext';
import { useCustomViewsAccess } from '../customViews/useCustomViews';
import ManifestTable from '../customViews/ManifestTable';
import ReportSummaryTable from '../customViews/ReportSummaryTable';
import { downloadCsv, safeFileName } from '../customViews/format';
import {
  customViewErrorCode, customViewErrorDetails, customViewsApi, CustomViewSurface, ReportHome, ReportResult,
} from '../../services/api';

const PAGE = 200;

interface ReportInfo {
  name: string;
  description: string | null;
  surface: CustomViewSurface;
  handle: string | null;
  canEdit: boolean;
  home: ReportHome;
}

const monthRange = () => {
  const now = new Date();
  return {
    from: format(new Date(now.getFullYear(), now.getMonth(), 1), 'yyyy-MM-dd'),
    to: format(now, 'yyyy-MM-dd'),
  };
};

const ReportRunPage: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const reportId = Number(id);
  const { orgId } = useOrganization();
  const access = useCustomViewsAccess(orgId, 'report');
  const [info, setInfo] = useState<ReportInfo | null>(null);
  const [range, setRange] = useState(monthRange);
  const [result, setResult] = useState<ReportResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<string[]>([]);
  const [shown, setShown] = useState(PAGE);

  const isBilling = info?.surface === 'billing';

  const run = useCallback(async (surface: CustomViewSurface | undefined, dates: { from: string; to: string }) => {
    setLoading(true);
    setErrors([]);
    try {
      const data = await customViewsApi.render(orgId, reportId,
        surface === 'billing' ? { from_date: dates.from, to_date: dates.to } : {}) as ReportResult;
      setResult(data);
      setShown(PAGE);
    } catch (err) {
      setResult(null);
      setErrors(customViewErrorCode(err) === 'view_not_found'
        ? ['This report no longer exists. It may have been archived.']
        : customViewErrorDetails(err));
    } finally {
      setLoading(false);
    }
  }, [orgId, reportId]);

  // Load the definition, then run it. Billing reports run for the current month first.
  useEffect(() => {
    if (!orgId || !Number.isFinite(reportId)) return;
    let cancelled = false;
    (async () => {
      try {
        const def = await customViewsApi.get(orgId, reportId, 'report');
        if (cancelled) return;
        setInfo({
          name: def.name, description: def.description, surface: def.surface,
          handle: def.handle ?? null, canEdit: def.can_edit, home: def.home ?? 'reports',
        });
        await run(def.surface, monthRange());
      } catch (err) {
        if (!cancelled) setErrors(customViewErrorDetails(err));
      }
    })();
    return () => { cancelled = true; };
  }, [orgId, reportId, run]);

  const exportCsv = () => {
    if (!result?.csv || !info) return;
    // The BOM makes Excel read the file as UTF-8, like the Billing Report export.
    downloadCsv(`﻿${result.csv}`, `${safeFileName(info.name)}.csv`);
  };

  const hidden = result?.hidden_billable_rows || 0;
  const handle = info?.handle || result?.view?.handle || null;

  let body: React.ReactNode;
  if (!orgId) {
    body = (
      <Callout.Root>
        <Callout.Icon><InfoCircledIcon /></Callout.Icon>
        <Callout.Text>Pick a clinic to run this report.</Callout.Text>
      </Callout.Root>
    );
  } else if (errors.length > 0 && !result) {
    body = (
      <Callout.Root color="red">
        <Callout.Icon><ExclamationTriangleIcon /></Callout.Icon>
        <Callout.Text>{errors.join(' ')}</Callout.Text>
      </Callout.Root>
    );
  } else if (!info || (loading && !result)) {
    body = <Text color="gray">Running report…</Text>;
  } else if (result?.stale) {
    body = (
      <Callout.Root color="orange">
        <Callout.Icon><ExclamationTriangleIcon /></Callout.Icon>
        <Callout.Text>
          This report uses fields that have changed and needs to be re-saved before it can run.
          {info.canEdit && access.canDesign ? ' Open it in the editor and save it again.' : ' Ask a clinic admin to re-save it.'}
        </Callout.Text>
      </Callout.Root>
    );
  } else if (result) {
    const summary = result.summary ?? result.manifest.summary;
    body = (
      <Flex direction="column" gap="3">
        {summary && (
          <Box style={{ overflowX: 'auto' }}>
            <ReportSummaryTable summary={summary} />
          </Box>
        )}
        <Flex align="center" gap="3" wrap="wrap">
          <Text size="2" color="gray" data-testid="report-total">
            {result.total_count} {result.total_count === 1 ? 'row' : 'rows'}
          </Text>
        </Flex>
        {result.truncated && (
          <Callout.Root color="amber" data-testid="report-truncated">
            <Callout.Icon><ExclamationTriangleIcon /></Callout.Icon>
            <Callout.Text>
              Showing the first {result.records.length.toLocaleString()} of {result.total_count.toLocaleString()} rows.
              Narrow the report to see the rest.
              {summary && (result.summary_complete === false
                ? ` The summary above covers only these ${result.records.length.toLocaleString()} rows.`
                : ' The summary above covers every row.')}
            </Callout.Text>
          </Callout.Root>
        )}
        {result.notice && (
          <Callout.Root>
            <Callout.Icon><InfoCircledIcon /></Callout.Icon>
            <Callout.Text>{result.notice}</Callout.Text>
          </Callout.Root>
        )}
        {hidden > 0 && (
          <Text size="2" color="gray" data-testid="report-hidden-rows">
            This report leaves out {hidden} billable {hidden === 1 ? 'row' : 'rows'}
            {result.billable_total !== undefined ? ` of ${result.billable_total}` : ''}. That is expected for a report.
          </Text>
        )}
        <Box style={{ overflowX: 'auto' }}>
          <ManifestTable manifest={result.manifest} records={result.records.slice(0, shown)}
            groups={result.groups} totals={result.totals} dense
            emptyText="No rows match this report." />
        </Box>
        {shown < result.records.length && (
          <Flex justify="center">
            <Button variant="soft" onClick={() => setShown((n) => n + PAGE)}>
              Show more ({result.records.length - shown} left)
            </Button>
          </Flex>
        )}
      </Flex>
    );
  }

  return (
    <ReportsLayout title={info?.name || 'Report'} subtitle={info?.description || undefined}
      backTo={info?.home === 'billing' ? '/billing' : '/reports'}>
      <Card mb="4">
        <Flex justify="between" align="center" wrap="wrap" gap="3">
          <Flex align="center" gap="2" wrap="wrap">
            {handle && (
              <>
                <Text size="1" color="gray">Use in Ask Phanova:</Text>
                <HandleChip handle={handle} />
              </>
            )}
          </Flex>
          <Flex align="center" gap="2" wrap="wrap">
            {isBilling && (
              <>
                <Text size="1" color="gray">Dates</Text>
                <input type="date" value={range.from} aria-label="Report from date"
                  onChange={(e) => setRange((r) => ({ ...r, from: e.target.value }))} />
                <input type="date" value={range.to} aria-label="Report to date"
                  onChange={(e) => setRange((r) => ({ ...r, to: e.target.value }))} />
              </>
            )}
            <Button variant="soft" disabled={!info || loading} onClick={() => void run(info?.surface, range)}>
              {result ? 'Refresh' : 'Run'}
            </Button>
            <Button variant="soft" disabled={!result?.csv} onClick={exportCsv}>
              <DownloadIcon /> Export CSV
            </Button>
            {handle && (
              <Button variant="soft" onClick={() => askAssistant(`#${handle}`)}>Ask Phanova</Button>
            )}
            {info?.canEdit && access.canDesign && (
              <Button variant="soft" onClick={() => navigate(`/reports/${reportId}/edit`)}>Edit</Button>
            )}
          </Flex>
        </Flex>
        {result?.period && (
          <Text as="div" size="1" color="gray" mt="2">
            Period: {result.period.from_date} to {result.period.to_date}
          </Text>
        )}
      </Card>
      {loading && result && <Text size="1" color="gray">Refreshing…</Text>}
      {body}
    </ReportsLayout>
  );
};

export default ReportRunPage;
